import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { copy } from "../copy";
import { useBeatPlayer } from "../audio";
import Icon, { type IconName } from "../components/Icon";
import ShareBackMe from "../components/ShareBackMe";
import SiteFooter from "../components/SiteFooter";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { fullPageLoad } from "../navigate";
import { useSession } from "../session";
import ActivityTab from "../contest/ActivityTab";
import ContestHeader from "../contest/ContestHeader";
import ContestTabs, { type ContestTab } from "../contest/ContestTabs";
import CoverPanel from "../contest/CoverPanel";
import DetailsTab from "../contest/DetailsTab";
import JoinTab from "../contest/JoinTab";
import ReviewsTab from "../contest/ReviewsTab";
import VoteTab from "../contest/VoteTab";
import { contestURL, normalizeActivity, normalizeReviews, useContestDetail, useContestList, useMarket } from "../contest/api";
import { primaryButton } from "../contest/ui";

function Notice({ testId, icon, eyebrow, heading, body, action }: {
  testId: string;
  icon: IconName;
  eyebrow?: string;
  heading: string;
  body: string;
  action: ReactNode;
}) {
  return (
    <div data-testid={testId} className="mt-16 max-w-xl rounded-2xl border border-ground-edge bg-ground-raised p-6 md:p-8">
      <Icon name={icon} className="h-6 w-6 text-chrome-dim" />
      {eyebrow && <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-chrome-dim">{eyebrow}</p>}
      <h1 className="mt-3 font-headline text-4xl uppercase leading-[0.95] text-chrome">{heading}</h1>
      <p className="mt-4 text-sm leading-relaxed text-chrome-dim">{body}</p>
      {action}
    </div>
  );
}

function sameName(a: string, b: string): boolean {
  return a !== "" && a.toLowerCase() === b.toLowerCase();
}

// /contest/:slug. One detail read decides everything; the tab lists load
// only for a contest that exists, and a tab with nothing in it is not shown.
export default function Contest() {
  const { slug = "" } = useParams();
  const [params] = useSearchParams();
  const pickParam = params.get("pick");
  const backParam = params.get("back");
  const c = copy.contest;
  const session = useSession();
  const { state, retry, setViewer } = useContestDetail(slug);
  const reducedMotion = usePrefersReducedMotion();
  const player = useBeatPlayer();

  const contest = state.status === "ready" ? state.contest : null;
  // Sponsorship contests stay on the Plush page, which Go serves on a full load.
  const sponsorship = contest?.is_sponsorship === true;
  const live = contest && !sponsorship ? contest.slug : null;

  const activity = useContestList(live ? contestURL(live, "/activity") : null, normalizeActivity);
  const reviews = useContestList(live ? contestURL(live, "/reviews") : null, normalizeReviews);
  const { market, setMarket } = useMarket(live);

  const tabs = useMemo<ContestTab[]>(() => {
    const list: ContestTab[] = ["details", "join"];
    if (market) list.push("vote");
    if (activity && activity.length > 0) list.push("activity");
    if (reviews && reviews.length > 0) list.push("reviews");
    return list;
  }, [market, activity, reviews]);

  const [tab, setTab] = useState<ContestTab>("details");
  // ?pick={producer id} from the homepage, or a Back Me link's
  // ?back={username}; ?pick wins when both name someone in the market.
  const open = market?.status === "open";
  const byID = open ? market.outcomes.find((o) => o.producer_id === pickParam) : undefined;
  const byName = open && backParam ? market.outcomes.find((o) => sameName(o.username, backParam)) : undefined;
  const initialPick = (byID ?? byName)?.producer_id ?? null;
  const wantsPick = initialPick !== null;
  // The viewer's own line, when they are entered in this open market.
  const own =
    open && contest?.viewer.is_entrant && session.kind !== "visitor"
      ? market.outcomes.find((o) => sameName(o.username, session.username))
      : undefined;
  useEffect(() => {
    if (wantsPick) setTab("vote");
  }, [wantsPick]);
  const active = tabs.includes(tab) ? tab : "details";
  // On a Home Turf Join tab the share lives in the entry panel, so the
  // header does not also carry one.
  const joinTabShare = active === "join" && market?.tier === "home_turf";
  // On a phone the tabs sit below the cover, so the pool's button brings the
  // Vote tab into view as well as opening it.
  const pickFromPool = () => {
    setTab("vote");
    requestAnimationFrame(() =>
      document.getElementById("panel-vote")?.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "nearest" }),
    );
  };

  useEffect(() => {
    if (sponsorship) fullPageLoad(`/contest/${encodeURIComponent(slug)}`);
  }, [sponsorship, slug]);

  useEffect(() => {
    document.title = contest ? c.title(contest.title) : c.loadingTitle;
  }, [contest, c]);

  let body: ReactNode = null;
  if (state.status === "notfound") {
    body = (
      <Notice
        testId="contest-not-found"
        icon="searchEye"
        eyebrow={c.notFoundEyebrow}
        heading={c.notFound}
        body={c.notFoundSub}
        action={
          <Link to="/contests" className={`${primaryButton} mt-6`}>
            {c.browse}
          </Link>
        }
      />
    );
  } else if (state.status === "error") {
    body = (
      <Notice
        testId="contest-error"
        icon="errorWarning"
        heading={c.error}
        body={c.errorSub}
        action={
          <button type="button" onClick={retry} className={`${primaryButton} mt-6`}>
            {c.retry}
          </button>
        }
      />
    );
  } else if (contest && !sponsorship) {
    body = (
      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:gap-10">
        <div className="min-w-0 lg:sticky lg:top-28 lg:self-start">
          <CoverPanel contest={contest} market={market} onPick={pickFromPool} />
        </div>
        <div className="min-w-0">
          <ContestHeader contest={contest} />
          {own && !joinTabShare && (
            <div className="mt-6">
              <ShareBackMe slug={contest.slug} contest={contest.title} username={own.username} />
            </div>
          )}
          <div className="mt-7">
            <ContestTabs
              tabs={tabs}
              active={active}
              onChange={setTab}
              counts={{ activity: activity?.length, reviews: reviews?.length }}
            />
          </div>
          <div role="tabpanel" id={`panel-${active}`} aria-labelledby={`tab-${active}`} className="mt-7">
            {active === "details" && <DetailsTab contest={contest} />}
            {active === "join" && (
              <JoinTab
                contest={contest}
                onViewer={setViewer}
                market={market}
                username={session.kind !== "visitor" ? session.username : undefined}
              />
            )}
            {active === "vote" && market && (
              <VoteTab
                contest={contest}
                market={market}
                onMarket={setMarket}
                initialPick={initialPick}
                player={player}
                reducedMotion={reducedMotion}
              />
            )}
            {active === "activity" && activity && <ActivityTab items={activity} />}
            {active === "reviews" && reviews && <ReviewsTab reviews={reviews} />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-ground font-sans text-chrome antialiased">
      <main className="px-4 pb-20 pt-24 md:px-6 md:pb-28 md:pt-32">
        <div className="mx-auto w-full max-w-[1240px]">
          <Link
            to="/contests"
            className="bf-focusable inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim transition-colors hover:text-chrome"
          >
            <Icon name="arrowLeft" className="h-4 w-4" />
            {c.allContests}
          </Link>
          {body}
        </div>
      </main>
      {player.elements}
      <SiteFooter />
    </div>
  );
}
